import { appendAiGrantPreferencesToUrl } from '../aiGrantPreferencesUrl';
import type { AiGrantPreferencesQuery } from '../aiGrantPreferencesUrl';
import { envFlag } from '../envFlag';

/** sessionStorage key for the Office editor beta chooser (classic vs OfficeGrant). */
export const OFFICE_EDITOR_CHOICE_KEY = 'filegrant.officeEditorChoice';

export type OfficeEditorChoice = 'classic' | 'officegrant';

/**
 * Beta flag from env. When true, FM may show the editor chooser and open OfficeGrant at `/open`.
 */
export function isOfficeGrantBetaEnabled(
  env: { VITE_APP_OFFICEGRANT_BETA?: string } = import.meta.env,
): boolean {
  return envFlag(env.VITE_APP_OFFICEGRANT_BETA);
}

export function getOfficeEditorChoice(
  storage: Pick<Storage, 'getItem'> = sessionStorage,
): OfficeEditorChoice | null {
  const value = storage.getItem(OFFICE_EDITOR_CHOICE_KEY);
  if (value === 'classic' || value === 'officegrant') {
    return value;
  }
  return null;
}

export function setOfficeEditorChoice(
  choice: OfficeEditorChoice,
  storage: Pick<Storage, 'setItem'> = sessionStorage,
): void {
  storage.setItem(OFFICE_EDITOR_CHOICE_KEY, choice);
}

/**
 * Resolves which editor path to use.
 * - Beta off → always classic (no dialog).
 * - Beta on + stored choice → that choice.
 * - Beta on + no stored choice → null (show dialog).
 */
export function resolveOfficeEditorChoice(options: {
  betaEnabled: boolean;
  storedChoice: OfficeEditorChoice | null;
}): OfficeEditorChoice | null {
  if (!options.betaEnabled) {
    return 'classic';
  }
  return options.storedChoice;
}

/**
 * Handoff URL for OfficeGrant Sprint 2 `/open` entry:
 * `${VITE_APP_OFFICEGRANT_URL}/open?fileId=…&refreshToken=…` + prefs (+ optional `r=1`).
 */
export function buildOfficeGrantOpenUrl(params: {
  officeGrantBaseUrl: string;
  fileId: string | number;
  refreshToken?: string | null;
  isReadOnly?: boolean;
  preferences: Omit<AiGrantPreferencesQuery, 'refreshToken'>;
}): string {
  const base = params.officeGrantBaseUrl.replace(/\/$/, '');
  const openUrl = new URL(`${base}/open`);
  openUrl.searchParams.set('fileId', String(params.fileId));
  if (params.isReadOnly) {
    openUrl.searchParams.set('r', '1');
  }
  return appendAiGrantPreferencesToUrl(openUrl.toString(), {
    ...params.preferences,
    refreshToken: params.refreshToken,
  });
}
